import { ChangeDetectorRef, Directive, EventEmitter, Input, OnDestroy, OnInit, Output, ViewRef, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { Attribute } from '@df/catalog/attribute/attribute';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { IProductConfigurableSelection, IProductDataV3, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Logger } from '@df/core/service/logger';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { bestMatch } from '@df/util/fn/best-match';
import defaults from 'lodash-es/defaults';
import each from 'lodash-es/each';
import head from 'lodash-es/head';
import isArray from 'lodash-es/isArray';
import isEmpty from 'lodash-es/isEmpty';
import map from 'lodash-es/map';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import pickBy from 'lodash-es/pickBy';
import { Subscription } from 'rxjs';

export interface IAttributeConfData<TProduct extends Product<IProductDataV3> = Product<IProductDataV3>> {
    index: string;
    model: Attribute;
    code: string;
    options: IAttributeConfDataOption<TProduct>[];
}

export interface IAttributeConfDataOption<TProduct extends Product<IProductDataV3> = Product<IProductDataV3>>
    extends Catalog.IAttributeOption {
    index: string;
    product: TProduct;
    /**
     * If option is salable (has matching product)
     */
    salable: boolean;
    /**
     * If option can be selected is selable or is family attribute
     */
    selectable: boolean;
}

export interface IConfigurableEvent<TProduct extends Product<IProductDataV3>> {
    data: IProductConfigurableSelection;
    vm: ProductConfigurableOptionsComponentDef<TProduct>;
}
export interface ISelectableCheckState {
    salable: boolean;
    isFamilyAttribute: boolean;
}
/**
 *
 *
 * ## Usage (from any product component)
 *
 * this will find best matching product
 * ```html
 * <product-configurable-options product='::product' on-change='matchBest'></product-configurable-options>
 * ```
 *
 * that will find product matching exactly selection
 * ```html
 * <product-configurable-options product='::product' on-change='matchExactly'></product-configurable-options>
 * ```
 */
@Directive()
export abstract class ProductConfigurableOptionsComponentDef<TProduct extends Product<IProductDataV3>> implements OnInit, OnDestroy {
    protected subscriptions: Subscription = new Subscription();

    private moduleConfig = inject(NCatalogUi.OPTIONS);
    /**
     * Storage type to use
     *
     * if 'true' hashStorage will be used
     */
    @Input()
    storage?: boolean;

    /**
     * Family level attributes:
     * - are preselected
     * - are skipped if all combinations are oos
     * - are not skipped when no relative combination
     * - reset other options when no direct match
     */
    @Input()
    familyAttrs?: string[];

    private _dataStorage: IProductConfigurableSelection | undefined;

    protected set dataStorage(dataStorage: IProductConfigurableSelection | undefined) {
        this._dataStorage = dataStorage && this.getStorageData(dataStorage);
        this.hashStorageService.set('selection', this._dataStorage);
    }

    protected get dataStorage(): IProductConfigurableSelection | undefined {
        return this.hashStorageService.get('selection');
    }

    /**
     * Array of attribute codes which will be preselected on init
     * @type {string[]}
     */
    @Input()
    preselectOptions?: string[];

    /**
     * If true attributes with only one option available will be preselected on init,
     * we can pass array of attributes codes for which we should use preselection
     * @type {boolean | string[]}
     */
    @Input()
    preselectSingles?: boolean | string[];

    /**
     * If you want to skip some attribute sin storage
     */
    @Input()
    storageOmit?: string[];

    /**
     * On change function to call
     *
     * Typical usage will be either
     *
     * (change)='this.matchBest($event.data)' (if you want to match product if any option is selected)
     * or
     * (change)='this.matchExactly($event.data)' (if you want to match product after all options are selected)
     *
     * See also productPick - it is the replacement for match best
     */
    @Output()
    selectionChange = new EventEmitter<IConfigurableEvent<TProduct>>();

    /**
     * Notififies about product selection when user picks an option
     *
     * Each option has best matching product assigned to it, when clicking
     * we notify about that selection
     *
     * It is more reliable and better performing then selectionChange
     *
     * @tutorial  (productPick)="setModel($event)"
     */
    @Output()
    productPick = new EventEmitter<TProduct>();

    /**
     * On data init function to call
     * If you want to force child selection at start (default child usually) then use:             *
     * on-init='this.matchBest'
     *
     * @type {function}
     */
    @Output()
    init = new EventEmitter<IConfigurableEvent<TProduct>>();

    @Output()
    dataChange = new EventEmitter<IConfigurableEvent<TProduct>>();

    /**
     * On pick function to call
     *
     * It is used when you need to override selection somehow
     *
     */
    @Output()
    filter = new EventEmitter();

    /**
     * Product model or product id
     * @type {Product}
     * @see set product
     */
    protected _product!: TProduct;

    /**
     * Parent product
     */
    protected _parent?: TProduct;

    /**
     * Attributes list (in terms of configuration not attribute models)
     */
    attributes!: IAttributeConfData<TProduct>[];

    /**
     * This is attributes too, but we only use it internally
     * to have full lsit of attr (and not the displayed ones)
     * We use it to cleanup data object
     */
    private configurablMap?: IAttributeConfData<TProduct>[];

    /**
     * Selection data
     */
    @Input()
    set data(data: IProductConfigurableSelection) {
        this.setData(data);
    }
    get data(): IProductConfigurableSelection {
        return this.getData();
    }
    protected _data: IProductConfigurableSelection = {};

    /**
     * Options of component
     */
    // options: IAttributeConfDataOption[] | undefined;

    /**
     * Copy of selected attrs (this.data) to be used in ng-options
     */
    productData?: { [code: string]: any };

    /**
     * If has been initiated already
     * @see $onInit
     * @type {boolean}
     */
    protected initiated = false;

    @NgInject(Logger)
    private logger!: Logger;

    @NgInject(App)
    protected app!: App;

    @NgInject(Router)
    private router!: Router;

    @NgInject(HashStorageService)
    private hashStorageService!: HashStorageService;

    /**
     * Local overrides in selection for the time of hovering
     */
    private previewState: { [attrCode: string]: IAttributeConfDataOption<TProduct> | undefined } = {};

    /**
     * class constructor
     */
    constructor(
        protected _ref: ChangeDetectorRef,
        protected timeoutService: TimeoutService
    ) {
        _ref.detach();
    }

    //////////////////////////////////////////////////
    //
    // PRODUCT SETTERS/GETTERS
    //
    //////////////////////////////////////////////////

    /**
     * Called on params changes
     *
     * When product is changed updates all data here
     * Note that onInit is triggered here (component binding)
     *
     */
    @Input()
    set product(product: TProduct) {
        this._product = product;
        if (product) {
            const parent = product.parent;

            if (!this.parent || this.parent.id !== parent.id) {
                if (this.parent) {
                    // we need to reset data as it may remain from the previous location
                    this.data = pickBy(this._dataStorage, i => typeof i !== 'undefined' && i !== null) || {};
                }
                this.parent = parent as TProduct;
            } else {
                if (this.product!.isSimple && this.data) {
                    Object.assign(this.data, this.product!.createSelectionObject() || {});
                }
                this.onDataChange();
            }

            this.productData = pick(this.product!.data, map(this.attributes, 'code'));
        }
    }

    /**
     * Gets current product
     */
    get product(): TProduct {
        return this._product;
    }

    /**
     * Sets parent
     */
    set parent(parent: TProduct | undefined) {
        if (this._parent !== parent) {
            this._parent = parent;
            this.emitInit();
        }
    }

    /**
     * Gets parent
     */
    get parent(): TProduct | undefined {
        return this._parent;
    }

    protected setData(data: IProductConfigurableSelection) {
        this._data = data;
        this.emitDataChange();
    }

    protected getData() {
        return this._data;
    }

    //////////////////////////////////////////////////
    //
    // EVENTS
    //
    //////////////////////////////////////////////////

    /**
     * Called on component initiation
     * If storage param is set then it will bind itself
     */
    ngOnInit() {
        this.initiated = true;
        this.emitInit();

        this.detectChanges();

        this.subscriptions.add(this.app.client.obs.breakpoint.subscribe(() => this.detectChanges()));
        this.subscriptions.add(
            this.router.events.subscribe(e => {
                if (e instanceof NavigationEnd) {
                    const product = this.app.meta.model as TProduct;

                    if (product?.type === Product.type) {
                        this.updateDataFromStorage();

                        if (product.parent.id === this.parent?.id) {
                            this.onDataChange();
                        }
                    }
                }
            })
        );
    }

    ngOnChanges(changes) {
        if (this.initiated) {
            this.detectChanges();
        }
    }
    /**
     * Cleanup
     * unsubscribes
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    private updateDataFromStorage() {
        this._dataStorage = this.dataStorage;
        if (this.storage && this._dataStorage) {
            this.data = this._dataStorage;
        }
    }

    /**
     * Calls on init callback ONLY if component is fully initiatd
     * we need to wait for initiation so that hash is read
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    protected emitInit() {
        this.updateDataFromStorage();

        if (this.initiated) {
            // initiate attributes
            this.configurablMap = this.getConfigurableMap();
            this.setAttributes(this.configurablMap);

            // initiate data model
            this.initSelection();
            // we need to reset data as attributes might changed
            // if (this.storageHandle) {
            //     this.storageHandle.next();
            // }

            // run callback
            this.init.next({ data: this.data, vm: this });

            // sync product with any selection that has been applied
            this.onDataChange();
        }
    }

    /**
     * if component onChange is set then notify
     */
    protected emitChange() {
        this.selectionChange.emit({ data: this.data, vm: this });
    }

    /**
     * if component onFilter is set then notify
     */
    protected emitFilter(data = this.data) {
        this.filter.emit({ data: this.data, vm: this });
    }

    /**
     * Emit every `data` change
     */
    protected emitDataChange(data = this.data) {
        this.dataChange.emit({ data: this.data, vm: this });
    }

    /**
     * Updates options (that depends on data)
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    onDataChange(forceEmittingChanges = false) {
        let hadInvalidData = false;
        // update options
        each(this.attributes, (attribute: IAttributeConfData<TProduct>) => {
            attribute.options = this.getOptionsMap(attribute.code);
            // reset selection of not allowed
            const value = this.data[attribute.code];
            if (value && !attribute.options.find(option => option.value === value)) {
                delete this.data[attribute.code];
                hadInvalidData = true;
            }
        });

        if (this.storage) {
            this.dataStorage = this.getStorageData(this.data);
        }
        if (hadInvalidData || forceEmittingChanges) {
            this.emitChange();
        }
        this.detectChanges();
    }

    getStorageData(data: IProductConfigurableSelection): IProductConfigurableSelection {
        if (this.storageOmit) {
            return omit(data, this.storageOmit);
        }
        return data;
    }

    //////////////////////////////////////////////////
    //
    // COMPONETN OPTIONS AND ATTRIBUTES
    //
    //////////////////////////////////////////////////

    protected setAttributes(attributes: IAttributeConfData<TProduct>[] | undefined) {
        this.attributes = attributes || [];
    }

    /**
     * Initiate data model
     * For configurable product we preselect:
     *      options passed in preselectOptions binding
     *      single options if preselectSingles binding is true
     */
    protected initSelection() {
        const isSimple = this.product.isSimple;
        const isConfigurable = this.product.isConfigurable;

        const data = this.data || {};
        // if there are attributes set in url we need to respect them so we need to pick other attrs to find vaild product
        let selectionSource: TProduct;
        if (isEmpty(data)) {
            selectionSource = this.product;
        } else {
            const children = this.parent?.matchChildren(data, true) || [];
            selectionSource = (children.find(i => !i.isOutOfStock) || children[0] || this.product) as TProduct;
        }

        const selectionObject = selectionSource.createSelectionObject();

        // remove invalid data entries (values that are not possible to be picked)
        each(data, (value, code) => {
            if (value && code && !this.configurablMap?.find(i => i.code === code)?.options.find(i => i.value === value)) {
                data[code] = undefined;
            }
        });

        if (isSimple) {
            defaults(data, selectionObject);
        } else if (isConfigurable) {
            const attrs = this.preselectOptions || this.familyAttrs;
            if (attrs && selectionObject) {
                defaults(data, pick(selectionObject, attrs));
            }

            if (this.preselectSingles) {
                const isPreselectSinglesArray = isArray(this.preselectSingles);

                this.attributes.forEach((attribute: IAttributeConfData<TProduct>) => {
                    if (
                        attribute.options.length === 1 &&
                        (!isPreselectSinglesArray || (<string[]>this.preselectSingles).includes(attribute.code))
                    ) {
                        data[attribute.code] = attribute.options[0].value;
                    }
                });
            }
        }
        this.data = data;
    }

    /**
     * Creates map of configurable attributes
     * For simple product it will return object containing values matching that project
     * For configurable product it will return object for its default child
     */
    protected getConfigurableMap(): IAttributeConfData<TProduct>[] | undefined {
        try {
            const codes = this.parent?.configurableAttributeCodes;

            if (codes) {
                return codes.map(code => {
                    return {
                        index: code, // unique object index we can use to
                        code,
                        model: this.app.resources.attribute.get(code),
                        options: this.getOptionsMap(code)
                    };
                });
            }
        } catch (err) {
            this.logger.error(err);
        }

        return undefined;
    }

    protected checkStockForOptionPrint(product: Product, code: string, option: Catalog.IAttributeOption): boolean {
        return product.parent?.isConfigurable && !product.isOutOfStock;
    }

    /**
     *
     */
    protected getOptionsMap(code: string): IAttributeConfDataOption<TProduct>[] {
        const parent = this.parent;

        if (!parent) {
            return [];
        }

        const isFamilyAttribute = this.isFamilyAttribute(code);

        return parent
            .getConfigurableAttributeOptions(code)
            .map((option: Catalog.IAttributeOption) => {
                // skip fully out of stock options
                if (this.shouldSkipOption(option, code, isFamilyAttribute)) {
                    return;
                }

                const filter = { ...this.data, [code]: option.value };
                this.emitFilter(filter);

                // we also filter out items that have no parent as they may be linked from another configurable family
                let products = parent?.matchChildren(filter, true)?.filter(i => i.parent?.isConfigurable) as Product<IProductDataV3>[];
                let product: Product<IProductDataV3>;

                // for non family attributes (if we do have family) skip options without relative matches
                if (!products.length) {
                    if (this.familyAttrs && !isFamilyAttribute) {
                        return;
                    }
                    products = parent
                        ?.matchChildren({ [code]: option.value }, true)
                        ?.filter(i => i.parent?.isConfigurable) as Product<IProductDataV3>[];

                    product = bestMatch<Product<IProductDataV3>, IProductDataV3>(products, filter, product => product.data!);
                } else {
                    product = head(products)!;
                }

                const salable = this.hasSalableProducts(products);

                return Object.assign(
                    {
                        index: `${product?.id || 0}.${option.value}`, // depreciated
                        product,
                        salable,
                        selectable: this.isOptionSelectable({ salable, isFamilyAttribute })
                    },
                    option
                );
            })
            .filter(i => !!i) as IAttributeConfDataOption<TProduct>[];
    }

    protected shouldSkipOption(option: Catalog.IAttributeOption, code: string, isFamilyAttribute: boolean): boolean {
        const parent = this.parent;
        return (
            isFamilyAttribute &&
            (!this.moduleConfig.pdp.configurable.respectStockSettings || !this.app.data.showOutOfStock) &&
            !parent?.matchChildren({ [code]: option.value }, true)?.find(i => this.checkStockForOptionPrint(i, code, option))
        );
    }

    protected hasSalableProducts(products: Product[]): boolean {
        return !!products.find((product: Product) => product.isSalable());
    }

    protected isOptionSelectable(state: ISelectableCheckState) {
        return state.salable || state.isFamilyAttribute;
    }

    /**
     * Atrtibute tracking function
     */
    attributeTrackFn(index: number, attribute: IAttributeConfData<TProduct>) {
        return attribute.code;
    }

    /**
     * Atrtibute tracking function
     */
    optionTrackFn(index: number, option: IAttributeConfDataOption<TProduct>): number {
        return option.value;
    }

    optionValueTrackFn(index: number, option: IAttributeConfDataOption<TProduct>): number {
        return option.value;
    }

    //////////////////////////////////////////////////
    //
    // SELECTION MANIPULATION
    //
    //////////////////////////////////////////////////

    pickProduct(product: TProduct) {
        this.productPick.next(product);
    }

    /**
     * Pick options
     */
    pick(attribute: string | IAttributeConfData<TProduct>, option: number | IAttributeConfDataOption<TProduct>) {
        if (this.data && attribute) {
            const value: number | undefined = typeof option === 'number' ? option : option ? option.value : undefined;
            const code: string | undefined = typeof attribute === 'string' ? attribute : attribute ? attribute.code : undefined;

            if (value && code) {
                if (this.canUseOption(code, value)) {
                    this.data[code] = value;
                } else if (this.isFamilyAttribute(code)) {
                    each(this.data, (v, key) => delete this.data[key]);
                    this.data[code] = value;
                }
            }
        }

        this.emitFilter();
        this.onDataChange(true);
    }

    /**
     * Pick options
     */
    isPicked(attribute: string | IAttributeConfData<TProduct>, option: number | IAttributeConfDataOption<TProduct>) {
        const value: number | undefined = typeof option === 'number' ? option : option ? option.value : undefined;
        return this.getSelection(attribute) === value;
    }

    getSelection(attribute: string | IAttributeConfData<TProduct>): undefined | number {
        const code: string | undefined = typeof attribute === 'string' ? attribute : attribute ? attribute.code : undefined;
        const override = !this.app.client.isTouchy && code && this.previewState[code];
        if (override) {
            return override.value;
        }
        const selection = code && ((this.data && this.data[code]) || this.product.data![code]);
        return selection && typeof selection === 'number' ? selection : undefined;
    }

    hasSelection(attribute: string | IAttributeConfData<TProduct>): boolean {
        return this.getSelection(attribute) !== undefined;
    }

    /**
     * Checks if option can be picked (excl family attrs that are handled separately)
     */
    canUseOption(attributeCode: string, optionValue: number): boolean {
        return !!this.getOption(attributeCode, optionValue)?.selectable;
    }

    protected getOption(attributeCode: string, optionValue: number): IAttributeConfDataOption<TProduct> | undefined {
        return this.attributes.find(i => i.code === attributeCode)?.options.find(i => i.value === optionValue);
    }

    protected isFamilyAttribute(attributeCode: string): boolean {
        return Array.isArray(this.familyAttrs) && this.familyAttrs.includes(attributeCode);
    }

    protected detectChanges(sync = false) {
        if (this.initiated) {
            const cb = () => {
                if (!(<ViewRef>this._ref).destroyed) {
                    this._ref.detectChanges();
                }
            };

            sync ? cb() : this.timeoutService.async(cb);
        }
    }

    /**
     * Preview swatch
     */
    preview(attribute: IAttributeConfData<TProduct>, option: IAttributeConfDataOption<TProduct>): void {
        if (!this.app.client.isTouchy && this.previewState[attribute.code] !== option) {
            this.previewState[attribute.code] = option;
            this.detectChanges();
        }
    }

    /**
     * Leave preview mode
     */
    unpreview(attribute: IAttributeConfData<TProduct>, option: IAttributeConfDataOption<TProduct>): void {
        this.timeoutService.setTimeout(() => {
            if (!this.app.client.isTouchy && this.previewState[attribute.code] === option) {
                this.previewState[attribute.code] = undefined;
                this.detectChanges();
            }
        }, 200); // delay not to switch immediately when moving from one to another
    }

    protected toAttributeCode(attribute: string | IAttributeConfData<TProduct>): string | undefined {
        return typeof attribute === 'string' ? attribute : attribute ? attribute.code : undefined;
    }
}
